/**
 * SE-6488: the category tag chip shown beside the pipeline name in the board
 * header's title row (Figma node 80:14982, a Badge instance).
 *
 * The design frame renders exactly one pipeline — "New player acquisitions" —
 * and labels its chip **"Acquisitions"**, so that string is authoritative. The
 * other two seeded templates have no chip in the frame; their labels here are
 * the short form of the existing in-repo template labels
 * (`PIPELINE_TEMPLATE_OPTIONS`), NOT invented product copy. Flagged for design
 * confirmation on SE-6488 alongside the Stages/Share/Tags question.
 *
 * A `CUSTOM` or absent template has no category, so no chip renders — the chip
 * is never filled with a placeholder.
 * @module features/player-kanban/constants/pipelineCategories
 */

import type { KanbanPipelineTemplate } from "@/generated/graphql";

/**
 * Template → category chip label, keyed by the enum's WIRE VALUES.
 *
 * Keyed by string literal rather than by `KanbanPipelineTemplate.X` on purpose:
 * dereferencing the enum at module scope makes this module explode under any
 * test that partially mocks `@/generated/graphql` (the enum comes back
 * `undefined` and the computed key throws at import time). The wire values are
 * the stable contract either way.
 *
 * `CUSTOM` is deliberately absent: a custom pipeline has no seeded category,
 * and inventing one would put unbacked copy on the surface.
 */
const CATEGORY_LABEL_BY_TEMPLATE: Readonly<Record<string, string>> = {
  NEW_ACQUISITIONS: "Acquisitions",
  ACADEMY_PROMOTIONS: "Academy",
  OUTGOING_TRANSFERS: "Transfers",
};

/**
 * Resolve the category chip label for a pipeline template.
 * @param template - The board's pipeline template, if any.
 * @returns The chip label, or `null` when the pipeline has no category
 *   (custom pipelines, legacy boards with no template, roster/watchlist boards).
 */
export const resolvePipelineCategoryLabel = (
  template: KanbanPipelineTemplate | null | undefined
): string | null => {
  if (!template) return null;
  return CATEGORY_LABEL_BY_TEMPLATE[template] ?? null;
};
